// i18n
import { translateInComponent } from 'infras/i18n';
import translations from './i18n.json';

import styled from '@emotion/styled';
import {
  signUpPath,
  dashboardPath,
  webPath,
  enterpriseInquiryPath,
  downloadsPath,
  apiPath,
  changelogListPath,
} from 'presentations/routes/path';
import { useRouter } from 'next/router';
import {
  borderRadius,
  fontSize,
  fontWeight,
  mediaMax,
  mediaMin,
  spacing,
} from 'presentations/views/styles';
import Icon from 'presentations/views/components/atoms/Icon';
import Headline from 'presentations/views/components/atoms/Headline';
import Button from 'presentations/views/components/atoms/Button';
import {
  Appearance,
  background,
  black,
  blackFix,
  gray01,
  gray04,
  primary,
  whiteFix,
} from 'presentations/views/styles/color';
import Link from 'presentations/views/components/atoms/Link';
import LoginButton from 'presentations/views/components/atoms/LoginButton';
import SmallSignUpButton from 'presentations/views/components/atoms/SmallSignUpButton';
import { Provider } from 'domains/models/account/providerType';
import {
  ChangelogListItem,
  CustomerStoryListItem,
} from 'presentations/api/schema/contents';
import { ViewerItem } from 'presentations/api/schema/user';
import { imagePath } from 'presentations/assets';
import { ThemeProvider } from '@emotion/react';
import CustomerSlideList from '../CustomerSlideList';
import { useRequest } from 'presentations/views/hooks/request';

interface Props {
  changelog?: ChangelogListItem | null;
  size?: 'large' | 'medium';
  onClickInquiry?: () => void;
  isShowSignUp?: boolean;
  viewer: ViewerItem | null;
  image?: {
    width?: number;
    height?: number;
    url: string;
  };
}

export default function Component(props: Props) {
  const t = translateInComponent(translations);
  const router = useRouter();
  const customersRequest = useRequest(
    apiPath('/v1/contents/customers', {
      perPage: 10,
      locale: router.locale,
    }),
  );
  const customers: CustomerStoryListItem[] = customersRequest.data || [];

  return (
    <ThemeProvider theme={{ service: 'www', appearance: Appearance.Dark }}>
      <Container>
        <CoverImage
          image={imagePath('pages/top/202402/cover.png', {
            width: 714,
            height: 641,
            dpr: 2,
          })}
        />
        {props.changelog && (
          <NewContent>
            <NewMark>New</NewMark>
            <NewTitle href={changelogListPath()}>
              {props.changelog.title}
            </NewTitle>
            <NewLinkIcon name="chevron-right" />
          </NewContent>
        )}
        <Main>
          <Title variant="h1">
            {t('copy.0')}
            <br />
            <TitlePrimary>{t('copy.1')}</TitlePrimary>
            {t('copy.2')}
          </Title>
          {props.isShowSignUp &&
            (!props.viewer ? (
              <>
                <ButtonList>
                  <ButtonListItem>
                    <SignUpText>{t('signup')}</SignUpText>
                    <SignUpButtonContainer>
                      <MobileSignUpButton
                        service={Provider.Google}
                        href={signUpPath({
                          provider: Provider.Google,
                          to: webPath(dashboardPath(), router.locale),
                          hiddenPasswordProvider: 1,
                        })}
                      />
                      <SignUpButton
                        variant="signup"
                        service={Provider.Google}
                        isServiceOnly
                        href={signUpPath({
                          provider: Provider.Google,
                          to: webPath(dashboardPath(), router.locale),
                          hiddenPasswordProvider: 1,
                        })}
                      />
                      <MobileSignUpButton
                        service={Provider.Microsoft}
                        href={signUpPath({
                          provider: Provider.Microsoft,
                          to: webPath(dashboardPath(), router.locale),
                          hiddenPasswordProvider: 1,
                        })}
                      />
                      <SignUpButton
                        variant="signup"
                        service={Provider.Microsoft}
                        isServiceOnly
                        href={signUpPath({
                          provider: Provider.Microsoft,
                          to: webPath(dashboardPath(), router.locale),
                          hiddenPasswordProvider: 1,
                        })}
                      />
                    </SignUpButtonContainer>
                    <SignUpOtherContainer>
                      <MailSignUpButton
                        href={signUpPath({
                          to: webPath(dashboardPath(), router.locale),
                        })}
                      >
                        {t('email signup')}
                      </MailSignUpButton>
                      <MailSignUpButton
                        onClick={props.onClickInquiry}
                        href={signUpPath({
                          to: webPath(enterpriseInquiryPath(), router.locale),
                        })}
                      >
                        {t('inquiry team')}
                      </MailSignUpButton>
                    </SignUpOtherContainer>
                  </ButtonListItem>
                </ButtonList>
              </>
            ) : (
              <ButtonList>
                <LoggedInButtonListItem>
                  <DashboardButton
                    size="large"
                    href={dashboardPath()}
                    color="primary"
                  >
                    {t('Dashboard')}
                  </DashboardButton>
                  <DownloadButton
                    size="large"
                    href={downloadsPath()}
                    color="primary"
                    variant="outlined"
                  >
                    {t('Download')}
                  </DownloadButton>
                </LoggedInButtonListItem>
              </ButtonList>
            ))}
        </Main>
        <SlideContainer>
          <CustomerSlideList customers={customers} />
        </SlideContainer>
      </Container>
    </ThemeProvider>
  );
}
const Container = styled.div`
  background: url(${imagePath('pages/top/202402/bg-grid.png', {
      width: 1283,
      height: 637,
      dpr: 2,
    })})
    no-repeat center top;
  background-size: 1283px auto;
  background-color: ${background};
  position: relative;
  height: 637px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  ${mediaMax.tablet} {
    height: initial;
    padding-top: 54px;
  }
`;
const Main = styled.div`
  margin-top: ${spacing(56)};
  margin-bottom: ${spacing(40)};
  ${mediaMax.tablet} {
    margin-top: 0;
    margin-bottom: ${spacing(16)};
    display: flex;
    flex-direction: column;
    align-items: center;
  }
`;

const Title = styled(Headline)`
  color: ${black};
  display: inline-block;
  font-size: ${fontSize(56)};
  font-weight: ${fontWeight('bold')};
  margin-bottom: ${spacing(24)};
  margin-left: 100px;
  line-height: 1.35em;
  letter-spacing: -0.04em;

  ${mediaMax.tablet} {
    padding: 0 ${spacing(20)};
    font-size: 6vw;
    margin-left: 0;
    text-align: center;
  }
`;
const TitlePrimary = styled.span`
  color: ${primary};
`;
const ButtonList = styled.div`
  position: relative;
  margin-left: 100px;
  ${mediaMax.tablet} {
    margin-left: 0;
  }
`;
const ButtonListItem = styled.div`
  display: inline-block;
  margin-bottom: ${spacing(20)};
  ${mediaMax.tablet} {
    margin-bottom: ${spacing(16)};
  }
`;
const LoggedInButtonListItem = styled.div`
  display: flex;
  /* justify-content: center; */
  margin-bottom: ${spacing(20)};
  ${mediaMax.tablet} {
    margin-bottom: ${spacing(16)};
    flex-direction: column;
    padding: 0 ${spacing(12)};
  }
`;
const MailSignUpButton = styled(Link)`
  color: ${black} !important;
  display: block;
  border-radius: ${borderRadius(6)};
  padding: ${spacing(6)} ${spacing(12)};
  font-size: ${fontSize(13)};
  text-decoration: underline;
`;
const SignUpButtonContainer = styled.div`
  display: flex;
  margin-bottom: ${spacing(16)};
`;
const SignUpButton = styled(LoginButton)`
  font-family: Gilroy, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto,
    Helvetica, Arial, sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji',
    'Segoe UI Symbol';
  -webkit-font-smoothing: antialiased;
  text-rendering: optimizelegibility;
  span {
    font-size: ${fontSize(14)};
  }

  margin-right: ${spacing(16)};
  :last-of-type {
    margin-right: 0;
  }
  ${mediaMax.tablet} {
    display: none;
  }
`;
const MobileSignUpButton = styled(SmallSignUpButton)`
  margin-right: ${spacing(12)};
  span {
    color: ${black};
    letter-spacing: 0em;
    font-size: ${fontSize(13)};
  }
  :last-of-type {
    margin-right: 0;
  }
  ${mediaMin.tablet} {
    display: none;
  }
`;
const SignUpOtherContainer = styled.div`
  display: flex;
  align-items: center;
  margin-top: ${spacing(4)};
  ${mediaMax.tablet} {
    flex-direction: column;
    justify-content: center;
  }
`;
const NewMark = styled.div`
  background-color: ${whiteFix};
  margin-right: ${spacing(8)};
  color: ${blackFix};
  font-size: ${fontSize(11)};
  font-weight: ${fontWeight('bold')};
  padding: 0 ${spacing(4)};
  border-radius: ${borderRadius(3)};
`;
const NewTitle = styled(Link)`
  margin-right: ${spacing(8)};
  color: ${black};
  max-width: calc(100vw / 3 * 2);
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;
const NewLinkIcon = styled(Icon)`
  color: ${gray01};
  width: 18px !important;
  height: 18px !important;
  margin-right: ${spacing(4)};
`;
const NewContent = styled.div`
  position: absolute;
  top: 104px;
  left: 0;
  right: 0;
  background-color: ${gray04}aa;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  width: fit-content;
  margin: 0 auto;
  padding: ${spacing(6)} ${spacing(8)};
  border-radius: ${borderRadius(4)};
  backdrop-filter: blur(2px);
  ${mediaMax.tablet} {
    position: static;
    margin-top: ${spacing(16)};
    margin-bottom: ${spacing(24)};
  }
`;

const SignUpText = styled.div`
  color: ${gray01};
  margin-bottom: ${spacing(8)};
  font-size: ${fontSize(12)};
  ${mediaMax.tablet} {
    text-align: center;
  }
`;
const CoverImage = styled.div<{ image: string }>`
  position: absolute;
  top: 0;
  left: 560px;
  bottom: 0;
  z-index: 0;
  background-image: url(${(props) => props.image});
  background-repeat: no-repeat;
  background-size: contain;
  width: 714px;
  height: 640px;
  /* background-position: center; */
  ${mediaMin.xl} {
    right: 0px;
    left: auto;
  }
  ${mediaMax.tablet} {
    display: none;
  }
`;

const DashboardButton = styled(Button)`
  background-color: ${whiteFix} !important;
  color: ${blackFix};
  min-width: 240px;
  ${mediaMax.tablet} {
    width: 100%;
  }
`;
const DownloadButton = styled(Button)`
  background-color: ${gray04} !important;
  color: ${whiteFix};
  min-width: 240px;
  margin-left: ${spacing(16)};
  :hover {
    color: ${whiteFix};
    background-color: ${primary};
  }
  ${mediaMax.tablet} {
    width: 100%;
    margin-left: 0;
    margin-top: ${spacing(16)};
  }
`;

const SlideContainer = styled.div`
  margin-bottom: ${spacing(32)};
  padding-bottom: ${spacing(16)};
  text-align: center;
  position: absolute;
  bottom: -32px;
  left: 0;
  right: 0;
  overflow: hidden;
  ${mediaMax.tablet} {
    position: static;
    margin-bottom: ${spacing(20)};
  }
`;
